import React from 'react';
import PropTypes from 'prop-types';
import {
  extend,
  params,
  customType,
  bool as BoolType,
  arrayOf as ArrayType,
  string as StringType,
  number as NumberType,
  useDataModel, alias,
} from '@thd-nucleus/data-sources';
import { useStore, useConfigService } from '@thd-nucleus/experience-context';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import {
  SpecialBuyPromosProductPod
} from './subcomponents/special-buy-product-pod/SpecialBuyPromosProductPod';
import { outOfStockCount, productSort, oosSwapping, alertIfServerItemOutOfStock } from '../utils';
import { ProductsGrid } from './ProductsGrid';

const { isATCEnabled } = podFulFillmentUtils;

export const ProductsRetrieval = ({
  itemIds, id, shouldSsrFirstProducts, backupItemIds, specialBuyType
}) => {
  const numSsrItemsConfig = useConfigService('fs:special-buy-promos--numSsrItems');
  const numSsrItems = typeof numSsrItemsConfig !== 'undefined'
    ? numSsrItemsConfig
    : 4;
  const store = useStore();
  const { storeId, storeZip } = store;

  const variables = {
    storeId,
    zipCode: storeZip,
    additionalSearchParams: {
      deliveryZip: storeZip
    }
  };

  let serverItemIds = [];
  let clientItemIds = itemIds;

  if (shouldSsrFirstProducts && !!numSsrItems) {
    serverItemIds = itemIds.slice(0, numSsrItems);
    clientItemIds = itemIds.slice(numSsrItems);
  }

  // 24 item limit imposed by API limitations
  const firstSetItems = clientItemIds?.slice(0, 24);
  const secondSetItems = clientItemIds?.slice(24, 48);

  const { data: serverProductDataSet } = useDataModel('specialBuyItems', {
    ssr: true,
    variables: {
      ...variables,
      itemIds: serverItemIds,
    },
    skip: !serverItemIds.length,
  });

  const { data: clientProductDataFirstSet } = useDataModel('specialBuyItems', {
    ssr: false,
    variables: {
      ...variables,
      itemIds: firstSetItems,
    },
    skip: !firstSetItems.length,
  });

  const { data: clientProductDataSecondSet } = useDataModel('specialBuyItems', {
    ssr: false,
    variables: {
      ...variables,
      itemIds: secondSetItems,
    },
    skip: !secondSetItems.length,
  });

  const serverProductSet = serverProductDataSet?.searchModel?.products || [];
  const clientProductFirstSet = clientProductDataFirstSet?.searchModel?.products || [];
  const clientProductSecondSet = clientProductDataSecondSet?.searchModel?.products || [];
  const clientProductSet = [...clientProductFirstSet, ...clientProductSecondSet];

  const serverProducts = !serverProductSet.length
    ? serverItemIds?.map((itemId) => ({ itemId, showPlaceholder: true }))
    : serverProductSet;

  const oosServerProducts = !serverProducts?.slice(-1)?.[0]?.showPlaceholder
    ? ([...serverProducts]).filter((product) => !isATCEnabled(product))
    : [];

  const clientProducts = !clientProductSet.length
    ? clientItemIds?.map((itemId) => ({ itemId, showPlaceholder: true }))
    : clientProductSet;

  const products = [...serverProducts, ...clientProducts].sort(productSort);

  const skipProductSwap = products?.slice(-1)?.[0]?.showPlaceholder || false;
  const oosCount = outOfStockCount(products);

  const backupItemIdsFirstSet = backupItemIds?.slice(0, 24);

  const skipBackupData = (!backupItemIdsFirstSet?.length || oosCount === 0) || skipProductSwap;

  const { data: backupData } = useDataModel('specialBuyItems', {
    ssr: false,
    variables: {
      ...variables,
      itemIds: backupItemIdsFirstSet,
    },
    skip: skipBackupData,
  });

  const backupProducts = backupData?.searchModel?.products?.length
    ? ([...backupData.searchModel.products]).filter((product) => isATCEnabled(product))
    : [];

  let fulfilledProducts = products;

  if (backupProducts.length && oosCount > 0 && !skipProductSwap) {
    fulfilledProducts = oosSwapping(products, backupProducts, oosCount);
  }

  if (oosServerProducts.length) {
    alertIfServerItemOutOfStock(oosServerProducts, specialBuyType);
  }

  return (<ProductsGrid id={id} products={fulfilledProducts} />);
};

ProductsRetrieval.displayName = 'ProductsRetrieval';

ProductsRetrieval.dataModel = extend({
  specialBuyItems: alias('searchModel').params({
    itemIds: ArrayType(StringType()),
    storeId: StringType(),
    isBrandPricingPolicyCompliant: BoolType(),
    additionalSearchParams: customType('AdditionalParams').shape({
      deliveryZip: StringType()
    })
  }).shape({
    products: params({ startIndex: NumberType() })
      .arrayOf(SpecialBuyPromosProductPod.dataModel.product)
  })
}, ProductsGrid);

ProductsRetrieval.propTypes = {
  itemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  id: PropTypes.string.isRequired,
  shouldSsrFirstProducts: PropTypes.bool.isRequired,
  backupItemIds: PropTypes.arrayOf(PropTypes.string),
  specialBuyType: PropTypes.string.isRequired
};

ProductsRetrieval.defaultProps = {
  backupItemIds: null
};
